import * as CheckboxPrimitive from "@radix-ui/react-checkbox"
import { normalizeTestId } from "@/lib/test-helper"
import { cn } from "@/lib/utils"

interface CheckboxProps extends React.ComponentPropsWithRef<typeof CheckboxPrimitive.Root> {
  testId?: string
  error?: boolean
  childClassName?: string
}

interface SVGProps extends React.SVGProps<SVGElement> {
  testId?: string | null
}

const DefaultCheckboxIcon = ({ className, testId }: SVGProps) => {
  return (
    <svg
      className={cn(
        "block h-6 w-6 shrink-0 peer-data-[state=checked]:hidden peer-disabled:peer-data-[state=checked]:hidden",
        className,
      )}
      data-testid={testId}
      viewBox="0 0 24 24"
      xmlns="http://www.w3.org/2000/svg">
      <path d="M19.5 3H4.5C4.10218 3 3.72064 3.15804 3.43934 3.43934C3.15804 3.72064 3 4.10218 3 4.5V19.5C3 19.8978 3.15804 20.2794 3.43934 20.5607C3.72064 20.842 4.10218 21 4.5 21H19.5C19.8978 21 20.2794 20.842 20.5607 20.5607C20.842 20.2794 21 19.8978 21 19.5V4.5C21 4.10218 20.842 3.72064 20.5607 3.43934C20.2794 3.15804 19.8978 3 19.5 3ZM19.5 19.5H4.5V4.5H19.5V19.5Z" />
    </svg>
  )
}

const CheckedCheckboxIcon = ({ className, testId }: SVGProps) => {
  return (
    <svg
      className={cn(
        "hidden h-6 w-6 shrink-0 peer-data-[state=checked]:block peer-disabled:peer-data-[state=checked]:block",
        className,
      )}
      data-testid={testId}
      viewBox="0 0 24 24"
      fill="none"
      xmlns="http://www.w3.org/2000/svg">
      <path d="M19.5 3H4.5C4.10218 3 3.72064 3.15804 3.43934 3.43934C3.15804 3.72064 3 4.10218 3 4.5V19.5C3 19.8978 3.15804 20.2794 3.43934 20.5607C3.72064 20.842 4.10218 21 4.5 21H19.5C19.8978 21 20.2794 20.842 20.5607 20.5607C20.842 20.2794 21 19.8978 21 19.5V4.5C21 4.10218 20.842 3.72064 20.5607 3.43934C20.2794 3.15804 19.8978 3 19.5 3ZM16.6406 10.2938L11.1469 15.5438C11.0048 15.6774 10.8169 15.7512 10.6219 15.75C10.4298 15.7507 10.2449 15.6768 10.1063 15.5438L7.35938 12.9188C7.28319 12.8523 7.22123 12.7711 7.17722 12.6801C7.1332 12.589 7.10805 12.49 7.10328 12.389C7.0985 12.2881 7.11419 12.1871 7.14941 12.0924C7.18463 11.9976 7.23865 11.9109 7.30822 11.8375C7.37779 11.7642 7.46148 11.7056 7.55426 11.6654C7.64703 11.6252 7.74697 11.6042 7.84808 11.6036C7.94919 11.603 8.04937 11.6229 8.14261 11.662C8.23584 11.7011 8.32021 11.7587 8.39062 11.8312L10.6219 13.9594L15.6094 9.20625C15.7552 9.07902 15.9446 9.01309 16.1379 9.02223C16.3312 9.03138 16.5135 9.1149 16.6467 9.25533C16.7798 9.39576 16.8535 9.58222 16.8524 9.77575C16.8513 9.96928 16.7754 10.1549 16.6406 10.2938Z" />
    </svg>
  )
}

const Checkbox = ({ error, testId, ...props }: CheckboxProps) => (
  <>
    <CheckboxPrimitive.Root className="peer" {...props} />
    <DefaultCheckboxIcon
      className={error ? "fill-critical" : "fill-on-surface-subdued"}
      testId={testId && normalizeTestId(testId, "-unchecked")}
    />
    <CheckedCheckboxIcon
      className={props.disabled ? "fill-on-surface opacity-60" : "fill-primary"}
      testId={testId && normalizeTestId(testId, "-checked")}
    />
  </>
)

export const SingleCheckbox = ({ className, ref, ...props }: CheckboxProps) => (
  <label
    className={cn(
      "hover:bg-surface-hovered focus:bg-surface-focused focus:outline-primary active:bg-surface-pressed has-focus-visible:bg-surface-focused has-focus-visible:outline-primary has-data-[state=checked]:text-primary-surface has-data-[state=checked]:hover:bg-primary-surface-hovered has-data-[state=checked]:focus:bg-primary-surface-focused has-data-[state=checked]:active:bg-primary-surface-pressed [&:has([data-state=checked]:focus-visible)]:bg-primary-surface-focused has-[[disabled]]:hover:bg-surface-disabled has-[[disabled]]:focus:bg-surface-disabled has-[[disabled]]:active:bg-surface-disabled flex h-10 w-10 cursor-pointer items-center justify-center rounded-full transition-all focus:outline-hidden focus:outline-2 has-focus-visible:outline-hidden has-[[disabled]]:cursor-not-allowed",
      className,
    )}
    data-testid={props.testId}>
    <Checkbox ref={ref} {...props} />
  </label>
)

export const DetailCheckbox = ({ className, childClassName, children, ref, ...props }: CheckboxProps) => {
  const { testId } = props

  return (
    <label
      className={cn(
        "border-border bg-surface hover:bg-surface-hovered focus:bg-surface-focused focus:outline-primary active:bg-surface-pressed has-focus-visible:bg-surface-focused has-focus-visible:outline-primary [&:has([data-state=checked]):has([disabled])]:border-surface has-data-[state=checked]:border-on-primary-surface has-data-[state=checked]:bg-primary-surface has-data-[state=checked]:text-primary-surface has-data-[state=checked]:hover:bg-primary-surface-hovered has-data-[state=checked]:focus:bg-primary-surface-focused has-data-[state=checked]:active:bg-primary-surface-pressed has-[[disabled]]:bg-surface-disabled has-[[disabled]]:text-on-surface-disabled has-[[disabled]]:hover:bg-surface-disabled has-[[disabled]]:focus:bg-surface-disabled has-[[disabled]]:active:bg-surface-disabled inline-flex cursor-pointer items-center self-start rounded-md border px-1.5 py-1 transition-all focus:outline-hidden focus:outline-2 has-focus-visible:outline-hidden has-[[disabled]]:cursor-not-allowed",
        className,
      )}
      data-testid={testId}>
      <Checkbox ref={ref} {...props} />
      {children && (
        <span
          className={cn(
            "text-on-surface peer-disabled:text-on-surface peer-data-[state=checked]:text-on-primary-surface peer-disabled:peer-data-[state=checked]:text-on-surface cursor-pointer pr-1.5 pl-2 text-sm leading-5 font-medium peer-disabled:cursor-not-allowed peer-disabled:opacity-60 peer-disabled:peer-data-[state=checked]:opacity-60",
            childClassName,
          )}
          data-testid={testId && normalizeTestId(testId, "-label")}>
          {children}
        </span>
      )}
    </label>
  )
}
